import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useReactToPrint } from "react-to-print";
import { ToastContainer, toast } from "react-toastify";
import {
  FaFileInvoiceDollar,
  FaTags,
} from "react-icons/fa";

import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";

import {
  approveExpenseClaim,
  createBudgetCode,
  createExpenseItem,
  deleteBudgetCode,
  deleteExpenseClaim,
  deleteExpenseItem,
  exportExpenseClaims,
  getBudgetCodes,
  getBudgetRubriques,
  getExpenseClaimById,
  getExpenseClaims,
  markExpenseClaimPaid,
  rejectExpenseClaim,
  updateBudgetCode,
  updateExpenseClaim,
  updateExpenseItem,
} from "../../../services/expenseServices";

import { getEventDetails } from "../../../services/eventServices";

import ExpenseFilters from "./components/ExpenseFilters";
import ExpenseStatsCards from "./components/ExpenseStatsCards";
import ExpenseClaimsTable from "./components/ExpenseClaimsTable";
import ExpenseClaimDetailsDrawer from "./components/ExpenseClaimDetailsDrawer";
import ExpenseClaimPrintSheet from "./components/ExpenseClaimPrintSheet";
import BudgetCodesManagerDrawer from "./components/BudgetCodesManagerDrawer";

import {
  cleanFilters,
  downloadBlob,
  getEventLabel,
  isManualExpenseClaim,
  sortClaims,
} from "./utils/expenseAdminFormat";

const EMPTY_FILTERS = {
  q: "",
  status: "",
  eventId: "",
  budgetCodeId: "",
  from: "",
  to: "",
};

const printPageStyle = `
  @page {
    size: A4 portrait;
    margin: 6mm;
  }

  @media print {
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    * {
      box-sizing: border-box;
    }

    a[href]::after {
      content: "";
    }
  }
`;

export default function ExpensePageAdmin() {
  useScrollToTop();

  const { user } = useUser();

  const currentUser = useMemo(() => {
    if (user) return user;

    try {
      return JSON.parse(
        localStorage.getItem("userInfo") || "null",
      );
    } catch {
      return null;
    }
  }, [user]);

  const validatorUserId =
    currentUser?.userId ??
    currentUser?.id ??
    null;

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState({});

  const [budgetCodes, setBudgetCodes] = useState([]);
  const [budgetRubriques, setBudgetRubriques] = useState([]);

  const [
    budgetCodesDrawerOpen,
    setBudgetCodesDrawerOpen,
  ] = useState(false);

  const [
    budgetCodesLoading,
    setBudgetCodesLoading,
  ] = useState(false);

  const [
    budgetRubriquesLoading,
    setBudgetRubriquesLoading,
  ] = useState(false);

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [actionLoading, setActionLoading] = useState("");

  const [eventDetailsMap, setEventDetailsMap] =
    useState({});

  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const [printLoadingId, setPrintLoadingId] =
    useState(null);

  const runPrint = useReactToPrint({
    contentRef: printRef,

    documentTitle: printData?.claim?.id
      ? `fiche_frais_${printData.claim.id}`
      : "fiche_frais",

    pageStyle: printPageStyle,

    onAfterPrint: () => {
      setPrintData(null);
    },
  });

  const loadBudgetCodes = useCallback(async () => {
    try {
      setBudgetCodesLoading(true);

      const data = await getBudgetCodes();

      setBudgetCodes(
        Array.isArray(data) ? data : [],
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de charger les codes budgétaires.",
      );
    } finally {
      setBudgetCodesLoading(false);
    }
  }, []);

  const loadBudgetRubriques = useCallback(async () => {
    try {
      setBudgetRubriquesLoading(true);

      const data = await getBudgetRubriques();

      setBudgetRubriques(
        Array.isArray(data) ? data : [],
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de charger les rubriques budgétaires.",
      );
    } finally {
      setBudgetRubriquesLoading(false);
    }
  }, []);

  /*
   * Les fiches manuelles utilisent un événement technique.
   * Il ne faut donc pas charger cet événement dans eventDetailsMap.
   */
  const loadEventDetailsForClaims = useCallback(
    async (rows = []) => {
      try {
        const ids = [
          ...new Set(
            (rows || [])
              .filter(
                (claim) =>
                  claim?.event_id &&
                  !isManualExpenseClaim(claim),
              )
              .map((claim) => claim.event_id),
          ),
        ];

        if (!ids.length) return;

        const results = await Promise.allSettled(
          ids.map((id) => getEventDetails(id)),
        );

        setEventDetailsMap((previousMap) => {
          const nextMap = { ...previousMap };

          results.forEach((result, index) => {
            const eventId = ids[index];

            if (
              result.status === "fulfilled" &&
              result.value
            ) {
              nextMap[eventId] = result.value;
            }
          });

          return nextMap;
        });
      } catch (error) {
        console.error(
          "Erreur lors du chargement des détails des événements :",
          error,
        );
      }
    },
    [],
  );

  const loadClaims = useCallback(
    async (params = appliedFilters) => {
      try {
        setLoading(true);

        const data = await getExpenseClaims(params);

        const rows = Array.isArray(data)
          ? data
          : [];

        setClaims(rows);

        await loadEventDetailsForClaims(rows);
      } catch (error) {
        console.error(error);

        toast.error(
          "Impossible de charger les fiches de frais.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      appliedFilters,
      loadEventDetailsForClaims,
    ],
  );

  const loadClaimDetails = useCallback(
    async (claimId) => {
      if (!claimId) return;

      try {
        setDetailLoading(true);

        const data =
          await getExpenseClaimById(claimId);

        setSelectedClaim(data);

        if (
          data?.event_id &&
          !isManualExpenseClaim(data) &&
          !eventDetailsMap[data.event_id]
        ) {
          try {
            const event =
              await getEventDetails(data.event_id);

            setEventDetailsMap((previousMap) => ({
              ...previousMap,
              [data.event_id]: event,
            }));
          } catch (error) {
            console.error(
              "Erreur détail événement :",
              error,
            );
          }
        }
      } catch (error) {
        console.error(error);

        toast.error(
          "Impossible de charger le détail de la fiche.",
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [eventDetailsMap],
  );

  useEffect(() => {
    loadBudgetCodes();
    loadBudgetRubriques();
  }, [
    loadBudgetCodes,
    loadBudgetRubriques,
  ]);

  useEffect(() => {
    loadClaims(appliedFilters);
  }, [
    appliedFilters,
    loadClaims,
  ]);

  useEffect(() => {
    if (!printData?.claim) return undefined;

    const timer = window.setTimeout(() => {
      if (!printRef.current) {
        toast.error(
          "La version imprimable n’est pas encore prête.",
        );

        setPrintData(null);
        return;
      }

      runPrint();
    }, 180);

    return () => window.clearTimeout(timer);
  }, [
    printData,
    runPrint,
  ]);

  const sortedClaims = useMemo(
    () => sortClaims(claims, sortBy, sortDir),
    [
      claims,
      sortBy,
      sortDir,
    ],
  );

  const handleChangeFilter = (field, value) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [field]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event?.preventDefault?.();

    setAppliedFilters(cleanFilters(filters));
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters({});
  };

  const handleRefresh = () => {
    loadClaims(appliedFilters);
    loadBudgetCodes();
    loadBudgetRubriques();

    if (selectedId) {
      loadClaimDetails(selectedId);
    }
  };

  const handleBudgetCodesChanged =
    useCallback(async () => {
      await loadBudgetCodes();
      await loadBudgetRubriques();
      await loadClaims(appliedFilters);

      if (selectedId) {
        await loadClaimDetails(selectedId);
      }
    }, [
      appliedFilters,
      loadBudgetCodes,
      loadBudgetRubriques,
      loadClaims,
      loadClaimDetails,
      selectedId,
    ]);

  const handleExport = async () => {
    try {
      setExporting(true);

      const blob =
        await exportExpenseClaims(appliedFilters);

      const date = new Date();

      const pad = (number) =>
        String(number).padStart(2, "0");

      const filename =
        `fiches_frais_admin_${date.getFullYear()}-` +
        `${pad(date.getMonth() + 1)}-` +
        `${pad(date.getDate())}.csv`;

      downloadBlob(blob, filename);

      toast.success("Export généré.");
    } catch (error) {
      console.error(error);

      toast.error("Échec de l’export.");
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    const target =
      field === "status"
        ? "status"
        : field;

    setSortBy((previousSortBy) => {
      if (previousSortBy === target) {
        setSortDir((previousDirection) =>
          previousDirection === "asc"
            ? "desc"
            : "asc",
        );

        return previousSortBy;
      }

      setSortDir("asc");

      return target;
    });
  };

  const openClaim = async (claimOrId) => {
    const id =
      typeof claimOrId === "number" ||
      typeof claimOrId === "string"
        ? Number(claimOrId)
        : claimOrId?.id;

    if (!id) return;

    setSelectedId(id);
    setDrawerOpen(true);

    await loadClaimDetails(id);
  };

  const handlePrintClaim = async (claimOrId) => {
    const id =
      typeof claimOrId === "number" ||
      typeof claimOrId === "string"
        ? Number(claimOrId)
        : claimOrId?.id;

    if (!id) return;

    try {
      setPrintLoadingId(id);

      const data =
        await getExpenseClaimById(id);

      let nextEventDetailsMap = {
        ...eventDetailsMap,
      };

      if (
        data?.event_id &&
        !isManualExpenseClaim(data) &&
        !nextEventDetailsMap[data.event_id]
      ) {
        try {
          const event =
            await getEventDetails(data.event_id);

          nextEventDetailsMap = {
            ...nextEventDetailsMap,
            [data.event_id]: event,
          };

          setEventDetailsMap((previousMap) => ({
            ...previousMap,
            [data.event_id]: event,
          }));
        } catch (error) {
          console.error(
            "Erreur détail événement impression :",
            error,
          );
        }
      }

      setPrintData({
        claim: data,
        eventDetailsMap: nextEventDetailsMap,
        token: Date.now(),
      });
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de préparer l’impression de la fiche.",
      );
    } finally {
      setPrintLoadingId(null);
    }
  };

  const refreshAfterWorkflow = async (claimId) => {
    await loadClaims(appliedFilters);
    await loadClaimDetails(claimId);
  };

  const handleApprove = async (
    adminCommentaire,
  ) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("approve");

      await approveExpenseClaim(
        selectedClaim.id,
        {
          validated_by_user_id:
            validatorUserId,

          admin_commentaire:
            adminCommentaire || null,
        },
      );

      toast.success("Fiche approuvée.");

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible d’approuver la fiche.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleReject = async (
    adminCommentaire,
  ) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("reject");

      await rejectExpenseClaim(
        selectedClaim.id,
        {
          validated_by_user_id:
            validatorUserId,

          admin_commentaire:
            adminCommentaire || null,
        },
      );

      toast.success("Fiche rejetée.");

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de rejeter la fiche.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleSaveClaim = async (payload) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("save-claim");

      await updateExpenseClaim(
        selectedClaim.id,
        payload,
      );

      toast.success("Fiche mise à jour.");

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier la fiche de frais.",
      );

      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleCreateItem = async (payload) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("create-item");

      await createExpenseItem(
        selectedClaim.id,
        payload,
      );

      toast.success(
        "Ligne de frais ajoutée.",
      );

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible d’ajouter la ligne.",
      );

      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleUpdateItem = async (
    itemId,
    payload,
  ) => {
    if (!itemId || !selectedClaim?.id) return;

    try {
      setActionLoading(
        `update-item-${itemId}`,
      );

      await updateExpenseItem(
        itemId,
        payload,
      );

      toast.success(
        "Ligne de frais mise à jour.",
      );

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier la ligne.",
      );

      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!itemId || !selectedClaim?.id) return;

    try {
      setActionLoading(
        `delete-item-${itemId}`,
      );

      await deleteExpenseItem(itemId);

      toast.success(
        "Ligne de frais supprimée.",
      );

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer la ligne.",
      );

      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteClaim = async () => {
    if (!selectedClaim?.id) return;

    if (
      ["approved", "paid"].includes(
        selectedClaim.statut,
      )
    ) {
      toast.error(
        "Les fiches approuvées ou payées ne peuvent pas être supprimées.",
      );

      return;
    }

    const confirmed = window.confirm(
      `Confirmer la suppression de la fiche de frais #${selectedClaim.id} ?\n\n` +
        "Cette action est définitive et doit aussi supprimer les justificatifs rattachés côté back.",
    );

    if (!confirmed) return;

    try {
      setActionLoading("delete-claim");

      await deleteExpenseClaim(
        selectedClaim.id,
      );

      toast.success(
        "Fiche de frais supprimée.",
      );

      setDrawerOpen(false);
      setSelectedClaim(null);
      setSelectedId(null);

      await loadClaims(appliedFilters);
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer la fiche de frais.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleMarkPaid = async (
    adminCommentaire,
  ) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("paid");

      await markExpenseClaimPaid(
        selectedClaim.id,
        {
          validated_by_user_id:
            validatorUserId,

          admin_commentaire:
            adminCommentaire || null,
        },
      );

      toast.success(
        "Fiche marquée comme payée.",
      );

      await refreshAfterWorkflow(
        selectedClaim.id,
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de passer la fiche en payée.",
      );
    } finally {
      setActionLoading("");
    }
  };

  /*
   * Les fiches manuelles ont toutes le même event_id technique.
   * Le filtre affiche donc une seule entrée globale :
   * "Événements hors calendrier".
   */
  const eventOptions = useMemo(() => {
    const map = new Map();

    claims.forEach((claim) => {
      if (!claim?.event_id) return;

      const value = String(claim.event_id);

      if (isManualExpenseClaim(claim)) {
        if (!map.has(value)) {
          map.set(value, {
            value,
            label:
              "Réunions et événements hors calendrier",
          });
        }

        return;
      }

      const label = getEventLabel(
        claim,
        eventDetailsMap,
      );

      if (!map.has(value)) {
        map.set(value, {
          value,
          label,
        });
      }
    });

    return Array.from(map.values()).sort(
      (optionA, optionB) =>
        optionA.label.localeCompare(
          optionB.label,
          "fr",
        ),
    );
  }, [
    claims,
    eventDetailsMap,
  ]);

  return (
    <div className="w-full pt-[84px] font-satoshi">
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />

      <main className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-7 text-white shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-white/10 ring-1 ring-white/15">
                <FaFileInvoiceDollar className="text-2xl" />
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                  Administration
                </div>

                <h1 className="mt-1 text-3xl font-black tracking-tight">
                  Gestion des fiches de frais
                </h1>

                <p className="mt-2 max-w-3xl text-sm text-slate-300">
                  Centralise les demandes, filtre les fiches,
                  contrôle les justificatifs, modifie les
                  fiches en cours et pilote le workflow de
                  validation jusqu’au paiement.
                </p>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-3 sm:items-end">
              <button
                type="button"
                onClick={() =>
                  setBudgetCodesDrawerOpen(true)
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-slate-100"
              >
                <FaTags />
                Gérer les codes budgétaires
              </button>

              <div className="rounded-3xl bg-white/10 px-5 py-4 text-right ring-1 ring-white/15">
                <div className="text-xs uppercase tracking-wide text-slate-300">
                  Fiches affichées
                </div>

                <div className="mt-1 text-3xl font-black">
                  {sortedClaims.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <ExpenseStatsCards claims={claims} />
        </div>

        <div className="mt-6">
          <ExpenseFilters
            filters={filters}
            onChange={handleChangeFilter}
            onSubmit={handleApplyFilters}
            onReset={handleResetFilters}
            onRefresh={handleRefresh}
            onExport={handleExport}
            budgetCodes={budgetCodes}
            eventOptions={eventOptions}
            loading={loading}
            exporting={exporting}
          />
        </div>

        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xl font-black text-slate-900">
                Liste des fiches
              </div>

              <div className="text-sm text-slate-500">
                Clique sur une ligne pour afficher le
                détail complet.
              </div>
            </div>

            <div className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200">
              Tri : {sortBy} · {sortDir}
            </div>
          </div>

          <ExpenseClaimsTable
            claims={sortedClaims}
            loading={loading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onOpen={openClaim}
            onPrint={handlePrintClaim}
            printingId={printLoadingId}
            selectedId={selectedId}
            eventDetailsMap={eventDetailsMap}
          />
        </div>
      </main>

      <ExpenseClaimDetailsDrawer
        open={drawerOpen}
        claim={selectedClaim}
        loading={detailLoading}
        onClose={() => setDrawerOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        onMarkPaid={handleMarkPaid}
        onDelete={handleDeleteClaim}
        onSaveClaim={handleSaveClaim}
        onCreateItem={handleCreateItem}
        onUpdateItem={handleUpdateItem}
        onDeleteItem={handleDeleteItem}
        actionLoading={actionLoading}
        eventDetailsMap={eventDetailsMap}
        budgetCodes={budgetCodes}
      />

      <BudgetCodesManagerDrawer
        open={budgetCodesDrawerOpen}
        onClose={() =>
          setBudgetCodesDrawerOpen(false)
        }
        budgetCodes={budgetCodes}
        budgetRubriques={budgetRubriques}
        loading={
          budgetCodesLoading ||
          budgetRubriquesLoading
        }
        onCreateCode={createBudgetCode}
        onUpdateCode={updateBudgetCode}
        onDeleteCode={deleteBudgetCode}
        onChanged={handleBudgetCodesChanged}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none fixed -left-[99999px] top-0"
      >
        {printData?.claim ? (
          <ExpenseClaimPrintSheet
            ref={printRef}
            claim={printData.claim}
            eventDetailsMap={
              printData.eventDetailsMap
            }
          />
        ) : null}
      </div>
    </div>
  );
}